import { useEffect, useMemo, useState } from 'react'
import {
  loadPreference,
  savePreference,
  resolveTheme,
  applyTheme,
  type ThemePreference,
  type ResolvedTheme,
} from '../lib/theme'

export interface ThemeState {
  preference: ThemePreference
  resolved: ResolvedTheme
  setPreference: (pref: ThemePreference) => void
}

export function useTheme(): ThemeState {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => loadPreference())
  // Track system dark-mode separately so useMemo re-runs on OS theme change
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches
  )

  const resolved = useMemo<ResolvedTheme>(
    () => resolveTheme(preference, systemDark),
    [preference, systemDark]
  )

  // Apply resolved theme to the DOM
  useEffect(() => {
    applyTheme(resolved)
  }, [resolved])

  // Listen for OS dark-mode changes when preference is 'system'
  useEffect(() => {
    if (preference !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => setSystemDark(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [preference])

  const setPreference = (pref: ThemePreference) => {
    savePreference(pref)
    setPreferenceState(pref)
  }

  return { preference, resolved, setPreference }
}
